import { useState, useEffect, useCallback } from 'react';
import { uploadImage } from '../api';
import API from '../api';


const s = {
  page: { minHeight: '100vh', backgroundColor: '#f5f5f3', fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
  loading: { padding: '40px', textAlign: 'center', color: '#aaa', fontSize: '13px' },
  topNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  navBrand: { fontSize: '15px', fontWeight: '700', color: '#1a1a1a', letterSpacing: '-0.3px' },
  navRight: { display: 'flex', alignItems: 'center', gap: '20px' },
  navLink: { fontSize: '13px', color: '#666', textDecoration: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 0 },
  subNav: { backgroundColor: '#fff', borderBottom: '0.5px solid #e8e8e8', padding: '0 32px', display: 'flex' },
  subNavBtn: { padding: '12px 16px', border: 'none', borderBottom: '2px solid transparent', backgroundColor: 'transparent', fontSize: '13px', color: '#aaa', cursor: 'pointer', fontWeight: '500' },
  subNavBtnActive: { color: '#1a1a1a', borderBottomColor: '#1a1a1a' },
  content: { maxWidth: '700px', margin: '0 auto', padding: '32px 24px' },
  error: { backgroundColor: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
  successMsg: { backgroundColor: '#f0faf4', color: '#2e7d32', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '13px' },
  card: { backgroundColor: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '8px', padding: '24px', marginBottom: '16px' },
  cardTitle: { fontSize: '15px', fontWeight: '600', color: '#1a1a1a', marginBottom: '16px' },
  fieldGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: {},
  label: { display: 'block', fontSize: '12px', fontWeight: '500', color: '#666', marginBottom: '6px' },
  input: { width: '100%', padding: '9px 12px', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', outline: 'none', color: '#1a1a1a', boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '9px 12px', border: '0.5px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', outline: 'none', color: '#1a1a1a', resize: 'vertical', boxSizing: 'border-box' },
  colourRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  colourPicker: { width: '40px', height: '40px', padding: '2px', border: '0.5px solid #e0e0e0', borderRadius: '6px', cursor: 'pointer' },
  colourHex: { fontSize: '12px', color: '#666', fontFamily: 'monospace' },
  colourPreview: { width: '40px', height: '40px', borderRadius: '6px', border: '0.5px solid #e0e0e0' },
  hint: { fontSize: '12px', color: '#aaa', marginBottom: '12px' },
  previewRow: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' },
  logoPreview: { width: '64px', height: '64px', objectFit: 'cover', borderRadius: '50%', border: '0.5px solid #e0e0e0' },
  coverPreview: { width: '180px', height: '64px', objectFit: 'cover', borderRadius: '6px', border: '0.5px solid #e0e0e0' },
  removeBtn: { background: 'none', border: 'none', color: '#c0392b', fontSize: '12px', cursor: 'pointer' },
  fileInput: { width: '100%', fontSize: '13px' },
  btnPrimary: { padding: '10px 24px', backgroundColor: '#1a1a1a', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' },
  btnDisabled: { padding: '10px 24px', backgroundColor: '#ccc', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500' },
};

function RestaurantProfile({ onBack, onNavigate }) {
  const restaurant = JSON.parse(localStorage.getItem('restaurant') || '{}');
  const [profile, setProfile] = useState({ name: '', description: '', address: '', brand_colour: '#c0392b', phone: '', instagram_handle: '', theme: 'light' });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchProfile = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await API.get('/restaurant/profile');
      const data = res.data;
      setProfile({ name: data.name || '', description: data.description || '', address: data.address || '', brand_colour: data.brand_colour || '#c0392b', phone: data.phone || '', instagram_handle: data.instagram_handle || '', theme: data.theme || 'light' });
      if (data.logo_url) setLogoPreview(data.logo_url.startsWith('http') ? data.logo_url : (process.env.REACT_APP_API_URL || '') + data.logo_url);
      if (data.cover_url) setCoverPreview(data.cover_url.startsWith('http') ? data.cover_url : (process.env.REACT_APP_API_URL || '') + data.cover_url);
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setError('Image must be under 10MB'); return; }
    const preview = URL.createObjectURL(file);
    if (type === 'logo') { setLogoFile(file); setLogoPreview(preview); }
    else { setCoverFile(file); setCoverPreview(preview); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      let logoUrl = null;
      let coverUrl = null;
      if (logoFile) { const fd = new FormData(); fd.append('image', logoFile); const r = await uploadImage(fd); logoUrl = r.data.url; }
      if (coverFile) { const fd = new FormData(); fd.append('image', coverFile); const r = await uploadImage(fd); coverUrl = r.data.url; }
      const token = localStorage.getItem('token');
      const payload = { ...profile };
      if (logoUrl) payload.logo_url = logoUrl;
      if (coverUrl) payload.cover_url = coverUrl;
      const res = await API.patch('/restaurant/profile', payload);
      const stored = JSON.parse(localStorage.getItem('restaurant') || '{}');
      localStorage.setItem('restaurant', JSON.stringify({ ...stored, name: res.data.name }));
      setSuccess('Profile saved.');
      setLogoFile(null);
      setCoverFile(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('restaurant'); window.location.href = '/'; };

  if (loading) return <div style={s.loading}>Loading...</div>;

  const nav = (tab) => { if (onNavigate) onNavigate(tab); else onBack(); };

  return (
    <div style={s.page}>
      <div style={s.topNav}>
        <span style={s.navBrand}>{restaurant.name || 'Digital Menu'}</span>
        <div style={s.navRight}>
          <a href={'/menu/' + restaurant.slug} target="_blank" rel="noreferrer" style={s.navLink}>View menu</a>
          <button style={s.navLink} onClick={handleLogout}>Sign out</button>
        </div>
      </div>
            <div style={s.subNav}>
        <button style={{...s.subNavBtn, ...('menus' === 'profile' ? s.subNavBtnActive : {})}} onClick={() => nav('menus')}>Menus</button>
        <button style={{...s.subNavBtn, ...('hours' === 'profile' ? s.subNavBtnActive : {})}} onClick={() => nav('hours')}>Hours</button>
        <button style={{...s.subNavBtn, ...('profile' === 'profile' ? s.subNavBtnActive : {})}} onClick={() => nav('profile')}>Profile</button>
        <button style={{...s.subNavBtn, ...('staff' === 'profile' ? s.subNavBtnActive : {})}} onClick={() => nav('staff')}>Staff</button>
        <button style={{...s.subNavBtn, ...('settings' === 'profile' ? s.subNavBtnActive : {})}} onClick={() => nav('settings')}>Settings</button>
      </div>

      <div style={s.content}>
        {error && <p style={s.error}>{error}</p>}
        {success && <p style={s.successMsg}>{success}</p>}
        <form onSubmit={handleSave}>
          <div style={s.card}>
            <h2 style={s.cardTitle}>Basic Info</h2>
            <div style={s.fieldGrid}>
              <div style={s.field}>
                <label style={s.label}>Restaurant Name</label>
                <input style={s.input} type="text" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} required />
              </div>
              <div style={s.field}>
                <label style={s.label}>Phone Number</label>
                <input style={s.input} type="text" placeholder="+234 801 234 5678" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} />
              </div>
              <div style={{ ...s.field, gridColumn: 'span 2' }}>
                <label style={s.label}>Description</label>
                <textarea style={s.textarea} value={profile.description} onChange={(e) => setProfile({ ...profile, description: e.target.value })} rows={2} />
              </div>
              <div style={{ ...s.field, gridColumn: 'span 2' }}>
                <label style={s.label}>Address</label>
                <input style={s.input} type="text" placeholder="e.g. Shop 7, Festival Mall, Festac" value={profile.address} onChange={(e) => setProfile({ ...profile, address: e.target.value })} />
              </div>
              <div style={s.field}>
                <label style={s.label}>Instagram Handle</label>
                <input style={s.input} type="text" placeholder="@yourhandle" value={profile.instagram_handle} onChange={(e) => setProfile({ ...profile, instagram_handle: e.target.value })} />
              </div>
              <div style={{ ...s.field, gridColumn: 'span 2' }}>
                <label style={s.label}>Menu Theme</label>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    type="button"
                    onClick={() => setProfile({ ...profile, theme: 'light' })}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', border: profile.theme === 'light' ? '2px solid #1a1a1a' : '0.5px solid #e0e0e0', background: '#f7f5f0', cursor: 'pointer', fontSize: '12px', fontWeight: profile.theme === 'light' ? '600' : '400', color: '#1a1a1a' }}
                  >
                    Light
                  </button>
                  <button
                    type="button"
                    onClick={() => setProfile({ ...profile, theme: 'dark' })}
                    style={{ flex: 1, padding: '10px', borderRadius: '6px', border: profile.theme === 'dark' ? '2px solid #1a1a1a' : '0.5px solid #e0e0e0', background: '#1a1a1a', cursor: 'pointer', fontSize: '12px', fontWeight: profile.theme === 'dark' ? '600' : '400', color: '#fff' }}
                  >
                    Dark
                  </button>
                </div>
              </div>
              <div style={s.field}>
                <label style={s.label}>Brand Colour</label>
                <div style={s.colourRow}>
                  <input type="color" value={profile.brand_colour} onChange={(e) => setProfile({ ...profile, brand_colour: e.target.value })} style={s.colourPicker} />
                  <span style={s.colourHex}>{profile.brand_colour}</span>
                  <div style={{ ...s.colourPreview, backgroundColor: profile.brand_colour }} />
                </div>
              </div>
            </div>
          </div>

          <div style={s.card}>
            <h2 style={s.cardTitle}>Logo</h2>
            <p style={s.hint}>Square image recommended. Displayed as avatar on your customer menu.</p>
            {logoPreview && (
              <div style={s.previewRow}>
                <img src={logoPreview} alt="Logo" style={s.logoPreview} />
                <button type="button" style={s.removeBtn} onClick={() => { setLogoFile(null); setLogoPreview(null); }}>Remove</button>
              </div>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleImageChange(e, 'logo')} style={s.fileInput} />
          </div>

          <div style={s.card}>
            <h2 style={s.cardTitle}>Cover Image</h2>
            <p style={s.hint}>Wide image recommended. Displayed at the top of your customer menu.</p>
            {coverPreview && (
              <div style={s.previewRow}>
                <img src={coverPreview} alt="Cover" style={s.coverPreview} />
                <button type="button" style={s.removeBtn} onClick={() => { setCoverFile(null); setCoverPreview(null); }}>Remove</button>
              </div>
            )}
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={(e) => handleImageChange(e, 'cover')} style={s.fileInput} />
          </div>

          <button style={saving ? s.btnDisabled : s.btnPrimary} type="submit" disabled={saving}>
            {saving ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>
    </div>
  );
}


export default RestaurantProfile;
